// =============================================================================
// GRAPHIQUES PANEL COLLAPSED - Onglet replié (120px large)
// =============================================================================

import React, { useState, useEffect } from 'react';
import { BarChart3, TrendingUp } from 'lucide-react';
import { cn } from '@/lib/utils';

interface GraphiquesPanelCollapsedProps {
  isVisible: boolean;
  onExpand: () => void;
  nombreChantiers: number;
}

/**
 * Onglet plié des Graphiques (120px × 400px)
 * Plus large que le Planning (90px) pour plus de visibilité
 */
const GraphiquesPanelCollapsed = ({
  isVisible,
  onExpand,
  nombreChantiers,
}: GraphiquesPanelCollapsedProps) => {
  const [shouldBounce, setShouldBounce] = useState(false);

  // Animation bounce à l'arrivée
  useEffect(() => {
    if (isVisible) {
      const startTimer = setTimeout(() => setShouldBounce(true), 600);
      const stopTimer = setTimeout(() => setShouldBounce(false), 2100);
      return () => {
        clearTimeout(startTimer);
        clearTimeout(stopTimer);
      };
    }
  }, []);

  if (!isVisible) return null;

  return (
    <button
      data-miro-target="rentabilite-graphiques-panel"
      onClick={onExpand}
      className={cn(
        // Position fixe bord droit, centré verticalement
        'fixed right-0 top-1/2 -translate-y-1/2 z-40',
        // Dimensions : 120px large × 400px hauteur (plus large que Planning)
        'w-[120px] min-h-[400px]',
        // Style visuel premium
        'bg-gradient-to-b from-card/98 to-card/90 backdrop-blur-md',
        'border border-r-0 border-border/50 rounded-l-2xl',
        'shadow-2xl shadow-black/40',
        // Contenu centré en colonne
        'flex flex-col items-center py-8 gap-5',
        // Hover effect
        'hover:w-[130px] hover:from-card hover:to-card/95 transition-all duration-200',
        'group cursor-pointer',
        // Animation bounce
        shouldBounce && 'animate-subtle-bounce'
      )}
    >
      {/* ═══ ICÔNE GRAPHIQUE + BADGE ═══ */}
      <div className="relative">
        <div className="p-4 bg-primary/10 rounded-xl group-hover:bg-primary/20 transition-colors">
          <BarChart3 className="w-8 h-8 text-primary group-hover:scale-110 transition-transform" />
        </div>
        <span className="absolute -top-2 -right-2 w-6 h-6 bg-primary text-primary-foreground text-xs font-bold rounded-full flex items-center justify-center shadow-lg">
          3
        </span>
      </div>

      {/* ═══ TEXTE "GRAPHIQUES" VERTICAL ═══ */}
      <div
        className="text-sm font-bold tracking-widest text-muted-foreground group-hover:text-foreground transition-colors"
        style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
      >
        GRAPHIQUES
      </div>

      {/* ═══ SÉPARATEUR ═══ */}
      <div className="w-12 h-px bg-border/50" />

      {/* ═══ ICÔNE TENDANCE ═══ */}
      <div className="p-3 bg-emerald-500/10 rounded-xl">
        <TrendingUp className="w-6 h-6 text-emerald-400" />
      </div>

      {/* ═══ TEXTE DESCRIPTION ═══ */}
      <div className="flex flex-col items-center text-center px-2">
        <span className="text-xs font-medium text-muted-foreground">
          Vue
        </span>
        <span className="text-xs font-medium text-muted-foreground">
          globale
        </span>
      </div>

      {/* ═══ COMPTEUR CHANTIERS ═══ */}
      {nombreChantiers > 0 && (
        <>
          <div className="w-12 h-px bg-border/50" />
          <div className="flex flex-col items-center">
            <span className="text-2xl font-black text-foreground leading-none">
              {nombreChantiers}
            </span>
            <span className="text-[10px] font-medium text-muted-foreground mt-1">
              chantier{nombreChantiers > 1 ? 's' : ''}
            </span>
          </div>
        </>
      )}
    </button>
  );
};

export default GraphiquesPanelCollapsed;
